/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import {
  Action,
  ImportActionStatus,
  Intent,
  Step
} from '../../../../../ts/action';
import { LaunchMode } from '../../../../../ts/skill';
import { CreateAsyncThunk } from '../../../../../ts/state';
import { TemplateWorkspace } from '../../../../../ts/templates';
import { selectIsAccessUpdatesEnabled } from '../../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../../ia/store/instance/selectors';
import { addErrorMessage } from '../../../../ia/store/messages/actions';
import {
  getActionSkillId,
  getAssistantId
} from '../../../../ia/store/session/selectors';
import { selectLaunchModeSettings } from '../../../../ia/store/skills/selectors';
import { createdObjectEvents } from '../../../../ia/util/trackers/segment';
import {
  duplicateAction as duplicateActionApi,
  importActions as importActionsApi,
  importActionsStatus as importActionsStatusApi
} from '../../../api/actions';
import { sendTransactions } from '../../../api/transactions';
import {
  generateActionId,
  generateActionIntentName,
  generateEntityName,
  generateStepId,
  isDefaultAction,
  isSystemEntity
} from '../../../util/actionUtil';
import {
  composeCreateActionTransaction,
  composeCreateEntityTransaction,
  composeCreateIntentTransaction,
  composeCreateVariableTransaction
} from '../../../util/transactionUtil';
import { updateCollection, listCollections } from '../../collections/actions';
import {
  getCollectionsActions,
  selectCurrentCollectionId
} from '../../collections/selectors';
import { listDataTypes } from '../../data-types/actions';
import { fetchEntities } from '../../entities/actions';
import { getEntities } from '../../entities/selectors';
import { fetchIntents } from '../../intents/actions';
import { getIntents } from '../../intents/selectors';
import { listVariables } from '../../variables/actions';
import { getVariables } from '../../variables/selectors';
import { listActions } from '../actions';

const cacheEntityRefsMap = (
  obj: any,
  targetEntityIds: Set<string>,
  entityRefsMap: Map<
    string,
    { key: string; collection: { [x: string]: string } }[]
  >
) => {
  if (typeof obj === 'object') {
    _.forOwn(obj, (value, key, collection) => {
      if (targetEntityIds.has(value)) {
        if (entityRefsMap.has(value)) {
          entityRefsMap.get(value).push({ key, collection });
        } else {
          entityRefsMap.set(value, [{ key, collection }]);
        }
      } else if (typeof value === 'object') {
        cacheEntityRefsMap(value, targetEntityIds, entityRefsMap);
      }
    });
  }
};

export const createActionsFromTemplate = createAsyncThunk<
  Action[],
  TemplateWorkspace,
  CreateAsyncThunk
>('actions/create-from-template', async (data, thunkApi) => {
  const { actions, entities, intents, variables } = data;
  const state = thunkApi.getState() as any; // convert the type to any as RootState doesn't have models
  const skillId = getActionSkillId(state);
  const existingEntities = new Set(Object.keys(getEntities(state)));
  const alreadyExistEntities = new Set(Object.keys(getEntities(state)));
  const existingActions = new Set(_.keys(state.models.actions));
  const existingIntents = new Set(Object.keys(getIntents(state)));
  const existingVariables = new Set(Object.keys(getVariables(state)));
  const newEntityIds = new Set(entities.map(entity => entity.entity));
  const intentActionMap = new Map<string, Action>();
  const entityRefsMap = new Map<
    string,
    { key: string; collection: { [x: string]: string } }[]
  >();
  try {
    // Deduplicate action id, we need to do this first as intent id can associate with action id
    // Skip the system actions (id 'welcome', 'anything_else', 'fallback', 'run_always'), we don't need to create them again.
    // Define intentsToSkip to hold all the intents that we need to skip Eg. 'fallback_connect_to_agent'
    const intentsToSkip = [];
    const filteredActions = actions.filter(action => {
      if (isDefaultAction(action.action)) {
        if (action?.condition && 'intent' in action.condition) {
          intentsToSkip.push(action.condition.intent);
        }
        return false;
      }
      return true;
    });

    filteredActions.forEach(action => {
      if (existingActions.has(action.action)) {
        let newActionId = generateActionId();
        while (existingActions.has(newActionId)) {
          newActionId = generateActionId();
        }
        action.action = newActionId;
        // Ignore replacing action ids for subactions for now.
        // When we need to do it, we should replace ids in
        // `action.sub_action_references[n].action`
        // `action.step['some_step'].resolver.invoke_action.action`
      }
      existingActions.add(action.action);
      let intent;
      if (action.condition) {
        if ('intent' in action.condition) {
          intent = action.condition.intent;
        } else if (
          'and' in action.condition &&
          'intent' in action.condition.and[0]
        ) {
          intent = action.condition.and[0].intent;
        }
      }
      if (intent) {
        intentActionMap.set(intent, action);
      }
      cacheEntityRefsMap(action, newEntityIds, entityRefsMap);
    });
    // Deduplicate intent id
    // Skip the system intent (id 'fallback_connect_to_agent'), we don't need to create it again.
    intents.forEach(intent => {
      if (!intentsToSkip.includes(intent.intent)) {
        if (existingIntents.has(intent.intent)) {
          const action = intentActionMap.get(intent.intent);
          let newIntentId = generateActionIntentName(action.action);
          while (existingIntents.has(newIntentId)) {
            newIntentId = generateActionIntentName(action.action);
          }
          intent.intent = newIntentId;
          if ('intent' in action.condition) {
            action.condition.intent = newIntentId;
          } else if (
            'and' in action.condition &&
            'intent' in action.condition.and[0]
          ) {
            action.condition.and[0].intent = newIntentId;
          }
        }
        existingIntents.add(intent.intent);
      }
    });
    // Deduplicate entity id
    // Skip the existing system entity, we don't need to create them again.
    entities.forEach(entity => {
      if (!isSystemEntity(entity.entity)) {
        if (existingEntities.has(entity.entity)) {
          let newEntityId = entity.entity;
          while (existingEntities.has(newEntityId)) {
            newEntityId = generateEntityName();
          }
          const oldEntityId = entity.entity;
          entity.entity = newEntityId;
          existingEntities.add(entity.entity);
          if (entityRefsMap.has(oldEntityId)) {
            entityRefsMap.get(oldEntityId).forEach(({ key, collection }) => {
              collection[key] = newEntityId;
            });
          }
        } else {
          existingEntities.add(entity.entity);
        }
      }
    });

    // Composing transaction api payload
    const transactions = [];
    // Ignore system intents
    intents.forEach(intent => {
      if (!intentsToSkip.includes(intent.intent)) {
        transactions.push(
          composeCreateIntentTransaction(intent, intent.intent)
        );
        createdObjectEvents.intent({
          object: skillId ? `${skillId}--${intent.intent}` : intent.intent,
          category: 'template',
          resultValue: 'success',
          successFlag: true
        });
      }
    });
    // Ignore system entities and existing entities
    entities.forEach(entity => {
      if (
        !isSystemEntity(entity.entity) ||
        !alreadyExistEntities.has(entity.entity)
      ) {
        transactions.push(
          composeCreateEntityTransaction(entity, entity.entity)
        );
        createdObjectEvents.entity({
          object: skillId ? `${skillId}--${entity.entity}` : entity.entity,
          category: 'template',
          resultValue: 'success',
          successFlag: true
        });
      }
    });

    // Ignore existing variables
    variables.forEach(variable => {
      if (!existingVariables.has(variable.variable)) {
        transactions.push(
          composeCreateVariableTransaction(variable, variable.variable)
        );
        createdObjectEvents.entity({
          object: skillId
            ? `${skillId}--${variable.variable}`
            : variable.variable,
          category: 'template',
          resultValue: 'success',
          successFlag: true
        });
      }
    });

    filteredActions.forEach(action => {
      transactions.push(composeCreateActionTransaction(action, action.action));
      createdObjectEvents.action({
        object: skillId ? `${skillId}--${action.action}` : action.action,
        category: 'template',
        resultValue: 'success',
        successFlag: true
      });
    });
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to sendTransactions
    const isNewIA = selectIsNewIa(state);
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(state);
    const assistantId = getAssistantId(state);

    await sendTransactions(
      skillId,
      {
        operations: transactions
      },
      useNewIAUpdatedAccess,
      assistantId
    );
    const currentCollectionId = selectCurrentCollectionId(state);

    if (currentCollectionId) {
      //Add action(s) to a collection
      const collectionsActions = getCollectionsActions(state);
      const collectionObject = collectionsActions.list.find(
        collection => collection.collection === currentCollectionId
      );
      const actionReferences = collectionObject?.action_references.map(el => ({
        action: el.action
      }));
      const newActions = [];
      transactions.forEach(el => {
        if (el?.type === 'action') {
          newActions.push({
            action: el.target_reference
          });
        }
      });

      const params = {
        collection: currentCollectionId,
        options: {
          title: collectionObject.title,
          action_references: [...actionReferences, ...newActions]
        }
      };
      await thunkApi.dispatch(updateCollection(params));
    }
    // Refresh collections data table
    await thunkApi.dispatch(listCollections());

    await thunkApi.dispatch(listActions(skillId));
    await thunkApi.dispatch(fetchIntents(skillId, { page_limit: 100000 }));
    await thunkApi.dispatch(listVariables(skillId));
    await thunkApi.dispatch(listDataTypes());
    await thunkApi.dispatch(fetchEntities(skillId, {}, true, true));

    return filteredActions;
  } catch (err) {
    thunkApi.dispatch(addErrorMessage(err));
    if (Array.isArray(err.errors)) {
      err.errors.forEach(e => thunkApi.dispatch(addErrorMessage(e)));
    }
    throw err;
  }
});

export const duplicateAction = createAsyncThunk<
  Action,
  Action,
  CreateAsyncThunk
>(
  'actions/duplicate-action',
  async (action, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const skillId = getActionSkillId(state);
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with only selectIsNewIa on call to duplicateAction
    const useNewIAUpdatedAccess =
      selectIsNewIa(state) && selectIsAccessUpdatesEnabled(state);
    const assistantId = getAssistantId(state);
    const { action: actionId, title } = action;
    try {
      const newAction = await duplicateActionApi(
        skillId,
        actionId,
        { title },
        useNewIAUpdatedAccess,
        assistantId
      );
      await dispatch(listActions(skillId));
      await dispatch(fetchIntents(skillId, { page_limit: 100000 }));
      await dispatch(fetchEntities(skillId, {}, true, true));
      return newAction as Action;
    } catch (err) {
      // The error here somehow could be an empty object
      return rejectWithValue(err);
    }
  }
);

export const createActionsFromIntents = createAsyncThunk<
  { count: number; ignored: string[] },
  Intent[],
  CreateAsyncThunk
>('actions/create-from-intents', async (intents, thunkApi) => {
  try {
    const state = thunkApi.getState();
    const actions = state.models.actions as Record<string, Action>;
    const existingActionIds = new Set(Object.keys(actions));
    const existingActionNames = new Set(
      Object.values(actions).map(action => action.title)
    );
    const skillId = getActionSkillId(state);
    const transactions = [];
    const ignoredIntents = [];
    let lastAction = actions?.welcome?.next_action;
    intents.forEach(intent => {
      if (intent.examples.length === 0) {
        ignoredIntents.push(intent.intent);
        return;
      }
      const newActionName = intent.intent.replace(/[_-]/g, ' ');
      if (existingActionNames.has(newActionName)) {
        ignoredIntents.push(intent.intent);
        return;
      }
      const stepId = generateStepId();
      let actionId = generateActionId();
      while (existingActionIds.has(actionId)) {
        actionId = generateActionId();
      }
      existingActionIds.add(actionId);
      const launchModeSettings = selectLaunchModeSettings(state) as LaunchMode;

      const intentId = generateActionIntentName(actionId);
      const newIntent = { intent: intentId, examples: intent.examples };
      const newAction: Action = {
        action: actionId,
        condition: { intent: intentId },
        next_action: lastAction,
        disambiguation_opt_out: false, // if disambiguation is true, we want want opt_out to be false,
        topic_switch: {
          allowed_from: true,
          allowed_into: true,
          never_return: false
        },
        launch_mode: launchModeSettings?.default,
        steps: [],
        title: newActionName,
        variables: [{ title: '', data_type: 'any', variable: stepId }]
      };
      const firstStep: Step = {
        invalid_references: {
          callout: 0,
          entity: 0,
          intent: 0,
          jump_to_step: 0,
          value: 0,
          variable: 0
        },
        step: stepId,
        variableTitle: 'No response',
        variable: stepId,
        resolver: { type: 'continue' }
      };
      newAction.steps.push(firstStep); //when normalized action object just has array of step id for steps
      transactions.push(
        composeCreateIntentTransaction(newIntent, newIntent.intent)
      );
      transactions.push(
        composeCreateActionTransaction(newAction, newAction.action)
      );
      lastAction = actionId;
    });
    if (transactions.length > 0) {
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to sendTransactions
      const isNewIA = selectIsNewIa(state);
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(state);
      const assistantId = getAssistantId(state);

      await sendTransactions(
        skillId,
        {
          operations: transactions
        },
        useNewIAUpdatedAccess,
        assistantId
      );

      const currentCollectionId = selectCurrentCollectionId(state);
      if (currentCollectionId) {
        //Add imported intent(s) to a collection
        const collectionsActions = getCollectionsActions(state);
        const collectionObject = collectionsActions.list.find(
          collection => collection.collection === currentCollectionId
        );
        const actionReferences = collectionObject?.action_references.map(
          el => ({
            action: el.action
          })
        );
        const newActions = [];
        transactions.forEach(el => {
          if (el?.type === 'action') {
            newActions.push({
              action: el.target_reference
            });
          }
        });

        const params = {
          collection: currentCollectionId,
          options: {
            title: collectionObject.title,
            action_references: [...actionReferences, ...newActions]
          }
        };
        await thunkApi.dispatch(updateCollection(params));
      }
      // Refresh collections data table
      await thunkApi.dispatch(listCollections());

      await thunkApi.dispatch(listActions(skillId));
    }
    return {
      count: intents.length - ignoredIntents.length,
      ignored: ignoredIntents
    };
  } catch (err) {
    return thunkApi.rejectWithValue(err);
  }
});

export const importActions = createAsyncThunk<
  { count: number; ignored: string[] },
  Action[],
  CreateAsyncThunk
>('actions/import-actions', async (actions, thunkApi) => {
  const state = thunkApi.getState();
  const skillId = getActionSkillId(state);

  const isNewIA = selectIsNewIa(state);
  const useNewIAUpdatedAccess = isNewIA && selectIsAccessUpdatesEnabled(state);
  const assistantId = getAssistantId(state);

  try {
    let task = await importActionsApi(
      skillId,
      {
        actions: actions
      },
      useNewIAUpdatedAccess,
      assistantId
    );
    while (task.status === ImportActionStatus.PROCESSING) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      task = await importActionsStatusApi(
        skillId,
        task.task_id,
        useNewIAUpdatedAccess,
        assistantId
      );
    }
    if (task.status === ImportActionStatus.FAILED) {
      throw task.errors;
    }

    // Refresh collections data table
    await thunkApi.dispatch(listCollections());
    await thunkApi.dispatch(listActions(skillId));

    return task.result;
  } catch (err) {
    throw thunkApi.rejectWithValue(err);
  }
});
